import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, ScanLine, Phone, MessageSquareText, FileText } from "lucide-react";
import { CONTACT } from "@/lib/data";
import { useI18n } from "@/i18n";

const SMS_HREF = `sms:+13056911516?&body=${encodeURIComponent(
  "Hi EWS! I'd like a fast quote for commercial waste / dumpster service. My business is:"
)}`;

/**
 * Mobile-only floating "dock" nav (Instagram-style) with the highest-intent actions.
 * Hidden on >= sm where the desktop floating CTAs + top nav take over.
 */
export default function MobileDock() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t, localePath } = useI18n();

  if (pathname.startsWith("/admin")) return null;

  const homePath = localePath("/");
  const scanPath = localePath("/savings-calculator");
  const quotePath = localePath("/quick-quote");
  const isActive = (p) => (p === homePath ? pathname === p : pathname === p || pathname.startsWith(p));

  const Item = ({ icon: Icon, label, to, testid }) => {
    const active = isActive(to);
    return (
      <Link to={to} data-testid={testid} aria-label={label}
        className="relative flex flex-col items-center gap-1 px-2 py-1 w-[19%] active:scale-95 transition-transform">
        <Icon size={21} className={active ? "text-emerald-300" : "text-white/65"} strokeWidth={active ? 2.4 : 2} />
        <span className={`text-[10px] font-semibold ${active ? "text-emerald-300" : "text-white/55"}`}>{label}</span>
        {active && <span className="absolute -top-0.5 w-1 h-1 rounded-full bg-emerald-400" />}
      </Link>
    );
  };

  return (
    <motion.nav
      initial={{ y: 90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.5, type: "spring", stiffness: 260, damping: 24 }}
      className="sm:hidden fixed inset-x-3 bottom-3 z-[70]"
      data-testid="mobile-dock"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="relative flex items-end justify-between bg-[#0E3B3B]/95 backdrop-blur-xl border border-white/10 rounded-[26px] shadow-[0_14px_40px_-6px_rgba(0,0,0,0.55)] px-3 pt-2 pb-2.5">
        <Item icon={Home} label={t("dock.home")} to={homePath} testid="dock-home" />
        <Item icon={ScanLine} label={t("dock.scan")} to={scanPath} testid="dock-scan" />

        {/* Center emphasized Call action */}
        <div className="w-[24%] flex flex-col items-center">
          <a href={CONTACT.phoneHref} data-testid="dock-call" aria-label={t("dock.call")}
            className="grid place-items-center w-14 h-14 -mt-8 rounded-full bg-emerald-500 text-[#0A2C2C] shadow-[0_10px_24px_-4px_rgba(16,185,129,0.8)] ring-4 ring-[#FBFAF8] active:scale-95 transition-transform">
            <span className="absolute w-14 h-14 -mt-8 rounded-full bg-emerald-400/50 animate-ping opacity-30" />
            <Phone size={23} strokeWidth={2.5} className="relative" />
          </a>
          <span className="text-[10px] font-bold text-emerald-300 mt-1">{t("dock.call")}</span>
        </div>

        <a href={SMS_HREF} data-testid="dock-text" aria-label={t("dock.text")}
          className="relative flex flex-col items-center gap-1 px-2 py-1 w-[19%] active:scale-95 transition-transform">
          <MessageSquareText size={21} className="text-white/65" />
          <span className="text-[10px] font-semibold text-white/55">{t("dock.text")}</span>
        </a>

        <button onClick={() => navigate(quotePath)} data-testid="dock-quote" aria-label={t("dock.quote")}
          className={`relative flex flex-col items-center gap-1 px-2 py-1 w-[19%] active:scale-95 transition-transform ${isActive(quotePath) ? "" : ""}`}>
          <FileText size={21} className={isActive(quotePath) ? "text-emerald-300" : "text-white/65"} />
          <span className={`text-[10px] font-semibold ${isActive(quotePath) ? "text-emerald-300" : "text-white/55"}`}>{t("dock.quote")}</span>
        </button>
      </div>
    </motion.nav>
  );
}
